/* eslint-disable no-use-before-define */
import { useWindowWidth } from '@react-hook/window-size';
import { FC, useEffect, useState } from 'react';
import { InstantSearch, SearchBox, Configure } from 'react-instantsearch';
import { CurrentRefinements, ResetFilters } from '../shared/components';
import { ArticlesSection, CollectionsSection, FiltersSection } from './sections';
import { ProductsSection } from '../shared/sections';
import { Filter } from '../shared/types';
import { AlgoliaUtil, InitialUIState, QueryParamsUtil } from '../shared/utils';
import { GraphQLProvider } from '../shared/context/GraphQLProvider';
import { ArticlesButton } from './sections/Articles/components/ArticlesButton';
import { SearchResultDataLayer } from './sections/SearchDataLayer';
import { CategoriesTranslationsLoader } from '../shared/translations/CategoriesTranslationsLoader';
import { AlgoliaEtimTranslationLoader } from '../shared/translations/EtimTranslationsLoader';
import { SearchQuerySync } from './components/SearchQuerySync';
import { ArticlesResultsNumber } from './components/ArticlesResultsNumber';
import { useSearchStore } from '../shared/states/searchStore';
import { useDisableSearchResultsStore } from '../shared/states/disableQuickSearchResultsStore';
import { useArticlesPage, useForceFirstProductPage } from '../shared/states/searchResultsPages';

const algoliaUtil = new AlgoliaUtil({
  appId: theme.algoliaId,
  apiKey: theme.algoliaApiKey,
  hitsPerPage: {
    products: 50,
    collections: 8,
    articles: 16,
  },
  indexNames: {
    product: theme.algoliaProductIndex,
    collection: theme.featureFlags.useCollectionOneIndex
      ? theme.algoliaCollectionIndex
      : `${theme.algoliaIndexPrefix}collections`,
    article: theme.featureFlags.usePagesOneIndex ? theme.algoliaPagesIndex : `${theme.algoliaIndexPrefix}pages`,
  },
});

const updateOrRemoveSearchQueryParam = (searchQuery: string | undefined, newURL: URL) => {
  if (searchQuery) {
    newURL.searchParams.set('q', searchQuery);
  } else {
    newURL.searchParams.delete('q');
  }
};

export const App: FC = () => {
  const queryParamsUtil = new QueryParamsUtil(window.location.search);
  const instantSearchParams = algoliaUtil.getInstantSearchParams();
  const algoliaIndexes = algoliaUtil.getIndexes();

  const windowWidth = useWindowWidth();
  const mobile = windowWidth < 680;

  const [showArticles, setShowArticles] = useState(queryParamsUtil.isArticles());

  const searchQuery = useSearchStore((store) => store.searchQuery);
  const setSearchQuery = useSearchStore((store) => store.setSearchQuery);

  const setDisableSearchResults = useDisableSearchResultsStore((store) => store.setDisableSearchResults);

  // Initialize once on mount
  useEffect(() => {
    const initialQuery = queryParamsUtil.decodeSearchQuery();

    setSearchQuery(initialQuery);
    setDisableSearchResults(true);
  }, [setSearchQuery]);

  const [productsUIState, setProductsUIState] = useState<InitialUIState>(queryParamsUtil.getInitialUIState(true));
  const nonProductUiState = queryParamsUtil.getInitialUIState(false);

  const articlesPage = useArticlesPage((store) => store.page);
  const setArticlesPage = useArticlesPage((store) => store.setPage);

  const forceFirstProductPage = useForceFirstProductPage((store) => store.force);
  const setForceFirstProductPage = useForceFirstProductPage((store) => store.setForce);

  const setUrlFromProductsUIState = (uiState: InitialUIState) => {
    const { page, refinementList, range, query, hierarchicalMenu } = uiState;

    const { origin, pathname } = window.location;
    const newURL = new URL(origin + pathname);

    newURL.searchParams.set('view', 'results');

    updateOrRemoveSearchQueryParam(searchQuery, newURL);

    const newPage = forceFirstProductPage ? 1 : page;

    if (newPage) {
      newURL.searchParams.set('page', newPage.toString());
      setForceFirstProductPage(false);
    }

    Object.entries(refinementList).forEach(([key, val]) => {
      newURL.searchParams.set(key, JSON.stringify(val));
    });

    if (range) {
      Object.entries(range).forEach(([key, val]) => {
        newURL.searchParams.set(key, val);
      });
    }

    if (hierarchicalMenu) {
      Object.entries(hierarchicalMenu).forEach(([key, val]) => {
        newURL.searchParams.set(key, JSON.stringify(val));
      });
    }

    window.history.replaceState({}, ``, newURL);
  };

  const translatedTitle =
    searchQuery === '' || !searchQuery
      ? theme.locales.searchTitleWithoutQuery
      : theme.locales.searchForRequest.replace(`{{ search_request }}`, searchQuery);

  useEffect(() => {
    const domain = window.location.host; // e.g. "tameson.com"
    const capitalizedDomain = `${domain.charAt(0).toUpperCase()}${domain.slice(1)}`;

    document.title = `${translatedTitle} | ${capitalizedDomain}`;
  }, [searchQuery]);

  // on init if we are on articles view with page>1, ensure that we will se always 1st page of product results.
  useEffect(() => {
    setForceFirstProductPage(showArticles);
  }, []);

  const onClickArticles = () => {
    const { origin, pathname } = window.location;
    const newURL = new URL(origin + pathname);

    newURL.searchParams.set('view', 'results');
    newURL.searchParams.set('type', 'articles');
    newURL.searchParams.set('page', articlesPage.toString());

    updateOrRemoveSearchQueryParam(searchQuery, newURL);

    window.history.replaceState({}, ``, newURL);

    setShowArticles(true);
  };

  const defaultFilters: Array<Filter> = [
    {
      attribute: 'tags_algolia',
      type: 'multiple-select',
      title: theme.locales.collections,
      opened: true,
    },
    {
      attribute: 'availability',
      type: 'multiple-select',
      title: theme.locales.availability,
      opened: false,
    },
    { attribute: 'vendor', type: 'multiple-select', title: theme.locales.vendor, opened: true },
    { attribute: 'price', type: 'price-range', title: theme.locales.price, opened: true },
  ];

  return (
    <GraphQLProvider>
      <CategoriesTranslationsLoader loadOnMount />
      <div className="sr-container">
        {showArticles && (
          <InstantSearch
            searchClient={instantSearchParams.searchClient}
            indexName={algoliaIndexes.article!}
            initialUiState={{ [algoliaIndexes.article!]: nonProductUiState }}
            // eslint-disable-next-line react/jsx-boolean-value
            insights={true}
            onStateChange={({ uiState, setUiState }) => {
              uiState[algoliaIndexes.article!].refinementList = {
                ...uiState[algoliaIndexes.article!].refinementList,
              };

              const { page } = uiState[algoliaIndexes.article!];

              const newURL = new URL(window.location.href);

              if (page) {
                setArticlesPage(page);
              } else {
                setArticlesPage(1);
                setSearchQuery(searchQuery);
              }

              newURL.searchParams.set('page', page?.toString() ?? '1');

              updateOrRemoveSearchQueryParam(searchQuery, newURL);

              window.history.replaceState({}, ``, newURL);

              setUiState(uiState);
            }}
          >
            <SearchQuerySync searchQuery={searchQuery} />
            <Configure
              hitsPerPage={instantSearchParams.hitsPerPage.articles}
              attributesToSnippet={['description:10']}
              snippetEllipsisText="…"
              removeWordsIfNoResults="allOptional"
            />

            <div className="sr-top-title-container">
              <h2 className="sr-title">{translatedTitle}</h2>
              <ArticlesResultsNumber />
            </div>

            <ArticlesSection
              searchQuery={searchQuery}
              algoliaUtil={algoliaUtil}
              hideSection={() => {
                if (forceFirstProductPage) {
                  setProductsUIState({ ...productsUIState, page: 1, query: searchQuery });
                }

                setUrlFromProductsUIState(productsUIState);
                setShowArticles(false);
              }}
            />

            {/* search box is required to search by query */}
            <SearchBox hidden />
          </InstantSearch>
        )}

        {!showArticles && (
          <>
            <InstantSearch
              searchClient={instantSearchParams.searchClient}
              indexName={algoliaIndexes.article!}
              initialUiState={{ [algoliaIndexes.article!]: nonProductUiState }}
            >
              <SearchQuerySync searchQuery={searchQuery} />
              <Configure
                hitsPerPage={instantSearchParams.hitsPerPage.articles}
                attributesToSnippet={['description:10']}
                snippetEllipsisText="…"
                removeWordsIfNoResults="allOptional"
              />

              <div className="sr-top-title-container">
                <h2 className="sr-title">{translatedTitle}</h2>
                <ArticlesButton onClick={onClickArticles} />
              </div>

              {/* search box is required to search by query */}
              <SearchBox hidden />
            </InstantSearch>

            <InstantSearch
              searchClient={instantSearchParams.searchClient}
              indexName={algoliaIndexes.collection!}
              initialUiState={{ [algoliaIndexes.collection!]: nonProductUiState }}
            >
              <SearchQuerySync searchQuery={searchQuery} />
              <Configure
                hitsPerPage={instantSearchParams.hitsPerPage.collections}
                attributesToSnippet={['description:10']}
                facetFilters={['template_suffix:-virtual']}
                snippetEllipsisText="…"
                removeWordsIfNoResults="allOptional"
              />

              <CollectionsSection algoliaUtil={algoliaUtil} mobile={mobile} />

              {/* search box is required to search by query */}
              <SearchBox hidden />
            </InstantSearch>

            <InstantSearch
              searchClient={instantSearchParams.searchClient}
              indexName={algoliaIndexes.product}
              initialUiState={{ [algoliaIndexes.product]: productsUIState }}
              // eslint-disable-next-line react/jsx-boolean-value
              insights={true}
              onStateChange={({ uiState, setUiState }) => {
                uiState[algoliaIndexes.product].refinementList = {
                  ...uiState[algoliaIndexes.product].refinementList,
                };

                setProductsUIState(uiState[algoliaIndexes.product] as InitialUIState);

                setUrlFromProductsUIState(uiState[algoliaIndexes.product] as InitialUIState);

                setUiState(uiState);
              }}
            >
              <SearchQuerySync searchQuery={searchQuery} />
              <Configure
                hitsPerPage={instantSearchParams.hitsPerPage.products}
                attributesToSnippet={['description:10']}
                snippetEllipsisText="…"
                removeWordsIfNoResults="allOptional"
              />
              <AlgoliaEtimTranslationLoader />

              {mobile ? null : (
                <>
                  <CurrentRefinements excludeAttributes={['query']} />
                  <ResetFilters />
                </>
              )}

              {mobile ? <FiltersSection baseFilters={defaultFilters} mobile={mobile} /> : null}

              <div className="sr-content-container">
                {mobile ? null : <FiltersSection baseFilters={defaultFilters} mobile={mobile} />}

                <ProductsSection algoliaUtil={algoliaUtil} mobile={mobile} />
              </div>

              {/* search box is required to search by query */}
              <SearchBox hidden />
              <SearchResultDataLayer searchTerm={searchQuery} algoliaUtil={algoliaUtil} />
            </InstantSearch>
          </>
        )}
      </div>
    </GraphQLProvider>
  );
};
